import { useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../shared/components/Icon';
import { clamp, useDockDrag } from './dockDrag';
import { DockTabs } from './DockTabs';
import type { Rect } from './dockModel';
import { describeAddress } from './panelCatalog';
import { useDockElements, useWorkspace, useWorkspaceActions } from './WorkspaceProvider';
import type { FloatingWindowState } from './workspaceState';

const minimumWindowWidth = 320;
const minimumWindowHeight = 200;

type ResizeGrip = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const grips: readonly ResizeGrip[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

/**
 * Documents that have been pulled out of the dock.
 *
 * A window is nothing more than another host for the same documents: it owns a rectangle
 * inside the canvas, a stacking order, and three states — laid out, rolled down to the
 * strip along the bottom, or filling the canvas. Dragging its title bar hands the whole
 * window to the same drop resolver a tab uses, which is how it finds its way back into
 * the dock.
 */
export function FloatingWindows() {
  const state = useWorkspace();
  const laid = state.windows.filter((window) => window.state !== 'minimized');
  if (state.windows.length === 0) return null;
  return (
    <>
      {laid.map((window) => <FloatingWindow key={window.id} window={window} />)}
      <MinimizedStrip />
    </>
  );
}

function FloatingWindow({ window: floating }: { window: FloatingWindowState }) {
  const { t } = useTranslation();
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const { registerBody, registerFrame, canvas } = useDockElements();
  const { startWindowDrag } = useDockDrag();

  const focused = state.activeWindowId === floating.id;
  const activePanel = floating.activePanelId === null ? undefined : state.panels[floating.activePanelId];
  const identity = activePanel === undefined ? null : describeAddress(activePanel.address, t);

  const attachFrame = useCallback((element: HTMLElement | null) => {
    registerFrame(floating.id, element);
    return () => registerFrame(floating.id, null);
  }, [floating.id, registerFrame]);

  const attachBody = useCallback((element: HTMLElement | null) => {
    registerBody(floating.id, element);
    return () => registerBody(floating.id, null);
  }, [floating.id, registerBody]);

  const canvasRect = () => canvas?.getBoundingClientRect() ?? new DOMRect(0, 0, 1280, 800);

  const beginResize = (event: ReactPointerEvent<HTMLElement>, grip: ResizeGrip) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget;
    element.setPointerCapture(event.pointerId);
    const bounds = canvasRect();
    const origin = { x: event.clientX, y: event.clientY };
    const start: Rect = floating.rect;

    const onMove = (moveEvent: PointerEvent) => {
      const deltaX = moveEvent.clientX - origin.x;
      const deltaY = moveEvent.clientY - origin.y;
      let { x, y, width, height } = start;

      if (grip.includes('e')) width = clamp(start.width + deltaX, minimumWindowWidth, bounds.width - start.x);
      if (grip.includes('s')) height = clamp(start.height + deltaY, minimumWindowHeight, bounds.height - start.y);
      if (grip.includes('w')) {
        const edge = clamp(start.x + deltaX, 0, start.x + start.width - minimumWindowWidth);
        width = start.width + (start.x - edge);
        x = edge;
      }
      if (grip.includes('n')) {
        const edge = clamp(start.y + deltaY, 0, start.y + start.height - minimumWindowHeight);
        height = start.height + (start.y - edge);
        y = edge;
      }
      dispatch({ type: 'moveWindow', windowId: floating.id, rect: { x, y, width, height } });
    };

    const onUp = () => {
      element.releasePointerCapture(event.pointerId);
      element.removeEventListener('pointermove', onMove);
      element.removeEventListener('pointerup', onUp);
    };

    element.addEventListener('pointermove', onMove);
    element.addEventListener('pointerup', onUp);
  };

  const toggleMaximize = () => {
    const bounds = canvasRect();
    if (floating.state === 'maximized') dispatch({ type: 'windowState', windowId: floating.id, state: 'normal' });
    else dispatch({ type: 'windowState', windowId: floating.id, state: 'maximized', canvas: { x: 0, y: 0, width: bounds.width, height: bounds.height } });
  };

  return (
    <section
      ref={attachFrame}
      className={`dock-window${focused ? ' dock-window--focused' : ''}${floating.state === 'maximized' ? ' dock-window--maximized' : ''}`}
      style={{ left: `${floating.rect.x}px`, top: `${floating.rect.y}px`, width: `${floating.rect.width}px`, height: `${floating.rect.height}px`, zIndex: 20 + floating.z }}
      aria-label={identity === null ? t('workspace.floatingWindow') : identity.title}
      onPointerDownCapture={() => {
        if (!focused) dispatch({ type: 'focusWindow', windowId: floating.id });
      }}
    >
      <header
        className="dock-window__bar"
        onPointerDown={(event) => {
          if (event.button !== 0 || floating.state === 'maximized') return;
          const bounds = event.currentTarget.closest('.dock-window')?.getBoundingClientRect();
          if (bounds === undefined) return;
          startWindowDrag(
            event,
            { id: floating.id, label: identity?.title ?? t('workspace.floatingWindow'), icon: identity?.icon ?? 'dashboard' },
            { x: bounds.left, y: bounds.top, width: bounds.width, height: bounds.height },
          );
        }}
        onDoubleClick={toggleMaximize}
      >
        <span className="dock-window__title">
          <Icon name={identity?.icon ?? 'dashboard'} className="icon icon--small" />
          <strong>{identity?.title ?? t('workspace.floatingWindow')}</strong>
          {identity?.detail == null ? null : <em>{identity.detail}</em>}
        </span>

        <span className="dock-window__controls">
          <button
            type="button"
            className="dock-window__control"
            aria-label={t('workspace.dockWindow')}
            title={t('workspace.dockWindow')}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => dispatch({ type: 'dockWindow', windowId: floating.id, target: { kind: 'group', groupId: state.activeGroupId, zone: 'center' } })}
          >
            <Icon name="dock" className="icon icon--small" />
          </button>
          <button
            type="button"
            className="dock-window__control"
            aria-label={t('workspace.minimize')}
            title={t('workspace.minimize')}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => dispatch({ type: 'windowState', windowId: floating.id, state: 'minimized' })}
          >
            <Icon name="minimize" className="icon icon--small" />
          </button>
          <button
            type="button"
            className="dock-window__control"
            aria-label={floating.state === 'maximized' ? t('workspace.restoreWindow') : t('workspace.maximize')}
            title={floating.state === 'maximized' ? t('workspace.restoreWindow') : t('workspace.maximize')}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={toggleMaximize}
          >
            <Icon name={floating.state === 'maximized' ? 'restore' : 'maximize'} className="icon icon--small" />
          </button>
          <button
            type="button"
            className="dock-window__control dock-window__control--close"
            aria-label={t('workspace.closeWindow')}
            title={t('workspace.closeWindow')}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => dispatch({ type: 'closeWindow', windowId: floating.id })}
          >
            <Icon name="close" className="icon icon--small" />
          </button>
        </span>
      </header>

      {floating.panelIds.length > 1 ? (
        <DockTabs hostId={floating.id} panelIds={floating.panelIds} activePanelId={floating.activePanelId} focused={focused} />
      ) : null}

      <div className="dock-window__body" ref={attachBody} />

      {floating.state === 'maximized'
        ? null
        : grips.map((grip) => (
          <span
            key={grip}
            className={`dock-window__grip dock-window__grip--${grip}`}
            onPointerDown={(event) => beginResize(event, grip)}
          />
        ))}
    </section>
  );
}

/** Rolled-down windows, kept where the eye can find them again. */
function MinimizedStrip() {
  const { t } = useTranslation();
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const minimized = state.windows.filter((window) => window.state === 'minimized');
  if (minimized.length === 0) return null;

  return (
    <div className="dock-taskbar" role="toolbar" aria-label={t('workspace.minimizedWindows')}>
      {minimized.map((window) => {
        const panel = window.activePanelId === null ? undefined : state.panels[window.activePanelId];
        const identity = panel === undefined ? null : describeAddress(panel.address, t);
        return (
          <button
            key={window.id}
            type="button"
            className="dock-taskbar__item"
            onClick={() => dispatch({ type: 'focusWindow', windowId: window.id })}
          >
            <Icon name={identity?.icon ?? 'dashboard'} className="icon icon--small" />
            <span>{identity?.title ?? t('workspace.floatingWindow')}</span>
            {window.panelIds.length > 1 ? <em>{window.panelIds.length}</em> : null}
          </button>
        );
      })}
    </div>
  );
}
